/**
 * SPA-side mirror of `@flint/domain/compliance/gst-calc`.
 *
 * Why a mirror instead of importing the domain:
 *
 *   - Domain code lives in Node-land — direct imports work but pull
 *     transitive deps (drizzle, postgres, …) that bloat the SPA bundle.
 *   - The composer recomputes totals on every keystroke for the live
 *     preview; a network round-trip per keystroke would feel sluggish.
 *   - The math is small + deterministic — re-implementing it is
 *     cheaper than wiring a build-time tree-shake for the domain.
 *
 * Same rules as the domain:
 *   - banker's rounding (half-to-even)
 *   - intra-state ⇒ CGST + SGST split (SGST gets the odd paise)
 *   - inter-state ⇒ IGST only
 *   - exactly one of `{cgst+sgst}` or `{igst}` is non-zero
 *
 * The server is the authority — `issueInvoice` recomputes from
 * persisted line inputs and overwrites the row's totals, so any drift
 * here is at worst a UI refresh delta the user sees once.
 *
 * Tested in `__tests__/gst-calc-shim.test.ts` against the same
 * scenarios as the domain to keep the two in lockstep.
 */

export interface ShimLineInput {
  qtyMilli: number;
  ratePaise: bigint;
  discountBps: number;
  taxRateBps: number;
}

export interface ShimLineComputed {
  grossPaise: bigint;
  discountPaise: bigint;
  taxablePaise: bigint;
  taxPaise: bigint;
  lineTotalPaise: bigint;
}

export type ShimTaxMode = "intra" | "inter";

export interface ShimInvoiceTotals {
  subtotalPaise: bigint;
  discountTotalPaise: bigint;
  taxableValuePaise: bigint;
  cgstPaise: bigint;
  sgstPaise: bigint;
  igstPaise: bigint;
  roundOffPaise: bigint;
  grandTotalPaise: bigint;
  taxMode: ShimTaxMode;
}

/** `companyStateCode === placeOfSupplyCode` ⇒ intra-state. */
export function deriveTaxMode(companyStateCode: string, placeOfSupplyCode: string): ShimTaxMode {
  return companyStateCode === placeOfSupplyCode ? "intra" : "inter";
}

/** Per-line breakdown — same math as the domain. */
export function computeLineTax(line: ShimLineInput): ShimLineComputed {
  const grossPaise = halfToEvenDivide(line.ratePaise * BigInt(line.qtyMilli), 1000n);
  const discountPaise = applyBp(grossPaise, line.discountBps);
  const taxablePaise = grossPaise - discountPaise;
  const taxPaise = applyBp(taxablePaise, line.taxRateBps);
  const lineTotalPaise = taxablePaise + taxPaise;
  return { grossPaise, discountPaise, taxablePaise, taxPaise, lineTotalPaise };
}

/** Invoice totals — sum lines + apply intra/inter split. */
export function computeInvoiceTotals(input: {
  lines: ShimLineComputed[];
  companyStateCode: string;
  placeOfSupplyCode: string;
  roundOffPaise?: bigint;
}): ShimInvoiceTotals {
  const subtotalPaise = sumPaise(input.lines.map((l) => l.grossPaise));
  const discountTotalPaise = sumPaise(input.lines.map((l) => l.discountPaise));
  const taxableValuePaise = sumPaise(input.lines.map((l) => l.taxablePaise));
  const totalTaxPaise = sumPaise(input.lines.map((l) => l.taxPaise));
  const taxMode = deriveTaxMode(input.companyStateCode, input.placeOfSupplyCode);

  let cgstPaise = 0n;
  let sgstPaise = 0n;
  let igstPaise = 0n;
  if (taxMode === "intra") {
    const half = totalTaxPaise / 2n;
    cgstPaise = half;
    sgstPaise = totalTaxPaise - half;
  } else {
    igstPaise = totalTaxPaise;
  }

  const roundOffPaise = input.roundOffPaise ?? 0n;
  const grandTotalPaise = taxableValuePaise + totalTaxPaise + roundOffPaise;

  return {
    subtotalPaise,
    discountTotalPaise,
    taxableValuePaise,
    cgstPaise,
    sgstPaise,
    igstPaise,
    roundOffPaise,
    grandTotalPaise,
    taxMode,
  };
}

// ── helpers (mirror of @flint/domain/money) ─────────────────────────

function sumPaise(values: bigint[]): bigint {
  let acc = 0n;
  for (const v of values) acc += v;
  return acc;
}

/** Banker's rounding via basis points: `applyBp(amt, 1800) = amt * 18%`. */
function applyBp(amount: bigint, bp: number): bigint {
  const product = amount * BigInt(bp);
  const quotient = product / 10000n;
  const remainder = product % 10000n;
  const half = 5000n;
  if (remainder < half) return quotient;
  if (remainder > half) return quotient + 1n;
  return (quotient & 1n) === 0n ? quotient : quotient + 1n;
}

/** Half-to-even integer divide (same shape as domain `halfToEvenDivide`). */
function halfToEvenDivide(numerator: bigint, denominator: bigint): bigint {
  const quotient = numerator / denominator;
  const remainder = numerator % denominator;
  const twiceRemainder = remainder * 2n;
  if (twiceRemainder < denominator) return quotient;
  if (twiceRemainder > denominator) return quotient + 1n;
  return (quotient & 1n) === 0n ? quotient : quotient + 1n;
}

// ── ADR-0020 foreign-currency line math ─────────────────────────────

export interface ShimLineForeignInput {
  qtyMilli: number;
  rateForeignMinor: bigint;
  discountBps: number;
  taxRateBps: number;
}

export interface ShimLineForeignComputed {
  grossForeignMinor: bigint;
  discountForeignMinor: bigint;
  taxableForeignMinor: bigint;
  taxForeignMinor: bigint;
  lineTotalForeignMinor: bigint;
}

/**
 * SPA-side mirror of `@flint/domain/compliance/gst-calc/computeLineForeign`.
 * Foreign minor units (USD cents etc.) instead of INR paise; same banker's
 * rounding throughout.
 */
export function computeLineForeign(line: ShimLineForeignInput): ShimLineForeignComputed {
  const grossForeignMinor = halfToEvenDivide(line.rateForeignMinor * BigInt(line.qtyMilli), 1000n);
  const discountForeignMinor = applyBp(grossForeignMinor, line.discountBps);
  const taxableForeignMinor = grossForeignMinor - discountForeignMinor;
  const taxForeignMinor = applyBp(taxableForeignMinor, line.taxRateBps);
  const lineTotalForeignMinor = taxableForeignMinor + taxForeignMinor;
  return {
    grossForeignMinor,
    discountForeignMinor,
    taxableForeignMinor,
    taxForeignMinor,
    lineTotalForeignMinor,
  };
}

/**
 * SPA-side mirror of `convertForeignRateToPaise`. `rateForeignMinor *
 * fxRateToInrPaise / 100`, banker's rounded.
 */
export function convertForeignRateToPaise(
  rateForeignMinor: bigint,
  fxRateToInrPaise: bigint,
): bigint {
  if (fxRateToInrPaise <= 0n) return 0n;
  if (rateForeignMinor < 0n) return 0n;
  return halfToEvenDivide(rateForeignMinor * fxRateToInrPaise, 100n);
}
